import { forwardRef, type TextareaHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: string;
  error?: string;
  requiredMark?: boolean;
  showCount?: boolean;
  containerClassName?: string;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  {
    label,
    hint,
    error,
    requiredMark,
    showCount = true,
    className,
    containerClassName,
    id,
    maxLength = 500,
    value,
    ...rest
  },
  ref,
) {
  const length = typeof value === 'string' ? value.length : 0;
  return (
    <div className={cn('w-full', containerClassName)}>
      {label ? (
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <label htmlFor={id} className="block text-sm font-medium text-foreground-800">
            {label}
            {requiredMark ? <span className="text-rose-600"> *</span> : null}
          </label>
          {showCount ? (
            <span className="text-xs text-foreground-400">
              {length}/{maxLength}
            </span>
          ) : null}
        </div>
      ) : null}
      <textarea
        id={id}
        ref={ref}
        maxLength={maxLength}
        value={value}
        aria-invalid={Boolean(error)}
        className={cn(
          'w-full rounded-md border bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 transition-colors duration-200 ease-smooth',
          error
            ? 'border-rose-300 focus:border-rose-400'
            : 'border-background-300 hover:border-background-400 focus:border-accent-400',
          'focus:outline-none focus:ring-4 focus:ring-accent-200/60 resize-y min-h-[104px]',
          className,
        )}
        {...rest}
      />
      {error ? (
        <p className="mt-1 text-xs text-rose-600">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-xs text-foreground-500">{hint}</p>
      ) : null}
    </div>
  );
});

export default Textarea;